import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import ResourcePicker from '../components/ResourcePicker';
import StandardPicker from '../components/StandardPicker';
import ToolEditor from '../components/ToolEditor';
import { renderInstruction, insertToken } from '../components/toolRefs';
import RequirementPicker from '../components/RequirementPicker';
import ProductFamilyPicker from '../components/ProductFamilyPicker';
import ProductNumberPicker from '../components/ProductNumberPicker';
import OptionTagPicker from '../components/OptionTagPicker';
import { ESD_CONTROLS, PPE_OPTIONS, COMPETENCY_OPTIONS } from '../components/qmsOptionLists';
import StepImageInput from './StepImageInput';

import ProcedureAuthoringMetadata from '../components/ProcedureAuthoringMetadata';
import ProcedureRevisionBar from '../components/ProcedureRevisionBar';

const API_BASE = process.env.REACT_APP_API_URL;
const ACCENT = '#2dd4bf';
const SUPPLIERS = ['gpv','ubiqconn','acmepoint','hk_ringer'];
const STATUS_COLORS = { Draft:'#94a3b8','In Review':'#60a5fa',Released:'#46BB89',Superseded:'#f59e0b',Obsolete:'#EC2124' };
const TRANSITIONS = { submit:{from:'Draft',to:'In Review',label:'Submit for Review'},revise:{from:'Released',to:'Draft',label:'Revise / New Draft'},approve:{from:'In Review',to:'Released',label:'Approve & Release'},reject:{from:'In Review',to:'Draft',label:'Reject'},obsolete:{from:null,to:'Obsolete',label:'Mark Obsolete'} };
const BLANK_STEP = { title:'', instruction:'', caution:'', imageUrl:'', acceptanceCriteria:'', requiresInspection:false, torqueSpec:'' };
const ACCEPTANCE_CLASSES = ['', 'Class 1', 'Class 2', 'Class 3'];
const csv = (v) => (Array.isArray(v) ? v : (v ? v.split(',').map(s=>s.trim()).filter(Boolean) : []));
// CSV fields are held as a raw string while editing (so commas type smoothly),
// then coerced to arrays on save. value handles both shapes.

function normalizeRole(r) {
  if (!r) return ''; if (Array.isArray(r)) return (r[0]||'').toLowerCase().trim();
  return String(r).toLowerCase().trim();
}
function StatusBadge({ status }) {
  const c = STATUS_COLORS[status]||'#666';
  return <span style={{ display:'inline-block',padding:'2px 10px',borderRadius:20,fontSize:11,
    fontWeight:700,letterSpacing:'0.05em',textTransform:'uppercase',background:c+'22',color:c }}>{status}</span>;
}
function ChipList({ items, accent }) {
  const arr = items || [];
  if (!arr.length) return <span style={{ fontSize:14, color:'var(--ni-text-muted)' }}>—</span>;
  return <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
    {arr.map(x => <span key={x} style={{ fontSize:12, fontWeight:700, background:accent+'22', color:accent, borderRadius:20, padding:'3px 10px' }}>{x}</span>)}
  </div>;
}

export default function AssemblyInstructionDetail() {
  const { id } = useParams(); const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedRevision = searchParams.get('revision') || '';
  const [availableRevisions, setAvailableRevisions] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const loadSequence = useRef(0);
  const { getToken } = useAuth(); const { user } = useUser();
  const role = normalizeRole(user?.publicMetadata?.role) || normalizeRole(user?.publicMetadata?.roles);
  const canManage = ['admin','quality-manager','production-engineer','service'].includes(role);
  const supplierKey = ['gpv','ubiqconn','acmepoint','hk_ringer'].includes(String(role||'').replace(/^test-/,''));

  const [entry, setEntry] = useState(null);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changeSummary, setChangeSummary] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [actionModal, setActionModal] = useState(null);
  const [actionNote, setActionNote] = useState('');
  const [error, setError] = useState('');
  const [usedInFlows, setUsedInFlows] = useState([]);

  const load = useCallback(async () => {
    const sequence = ++loadSequence.current;
    setLoading(true); setError(''); setEditing(false);
    try {
      const token = await getToken();
      const query = requestedRevision ? `?revision=${encodeURIComponent(requestedRevision)}` : '';
      const res = await window.fetch(`${API_BASE}/quality/assembly-instructions/${id}${query}`, { headers:{ Authorization:`Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.message || data.error || 'Could not load document');
      if (sequence === loadSequence.current) {
        setEntry(data.instruction); setForm(JSON.parse(JSON.stringify(data.instruction)));
        setAvailableRevisions(data.availableRevisions || []); setSnapshot(data.snapshot || null);
      }
    } catch (err) { if (sequence === loadSequence.current) { setError(err.message); setEntry(null); } }
    finally { if (sequence === loadSequence.current) setLoading(false); }
  }, [id, getToken, requestedRevision]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!id) return;
    getToken().then(token =>
      window.fetch(`${API_BASE}/quality/process-flows/used-in?resource=assembly&id=${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json()).then(d => { if (d.ok) setUsedInFlows(d.flows || []); }).catch(() => {})
    );
  }, [id, getToken]);

  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const toggleSupplier = s => {
    const cur = form.visibility?.suppliers||[];
    const next = cur.includes(s)?cur.filter(x=>x!==s):[...cur,s];
    setForm(f=>({...f,visibility:{...f.visibility,suppliers:next,supplierAccess:next.length?'Specific':'None'}}));
  };
  const addStep = () => setForm(f=>({...f,steps:[...(f.steps||[]),{...BLANK_STEP,stepNumber:(f.steps||[]).length+1}]}));
  const removeStep = i => setForm(f=>({ ...f, steps: f.steps.filter((_,j)=>j!==i).map((s,j)=>({...s,stepNumber:j+1})) }));
  const setStep = (i,k,v) => setForm(f=>({ ...f, steps: f.steps.map((s,j)=>j===i?{...s,[k]:v}:s) }));
  const setSafety = (k,v) => setForm(f=>({ ...f, safety: { ...(f.safety||{}), [k]:v } }));
  const cursorRef = useRef({});   // { [stepIndex]: caret position in instruction }
  const insertToolRef = (i, toolId) => setStep(i, 'instruction', insertToken(form.steps[i].instruction || '', cursorRef.current[i], toolId));

  const handleSave = async () => {
    if (requestedRevision || snapshot || entry?.currentStatus !== 'Draft') return;
    setSaving(true); setError('');
    try {
      const token = await getToken();
      const payload = { ...form, changeSummary, expectedVersion:entry.__v,
        estimatedMinutes: form.estimatedMinutes === '' || form.estimatedMinutes == null ? null : Number(form.estimatedMinutes),
        effectiveDate: form.effectiveDate || null,
        // CSV fields may be raw strings after editing — coerce back to arrays.
        referencedStandards: csv(form.referencedStandards),
        requiredCompetencies: csv(form.requiredCompetencies),
        safety: { ...(form.safety||{}), ppe: csv(form.safety?.ppe), hazardNotes: form.safety?.hazardNotes || '' } };
      const res = await window.fetch(`${API_BASE}/quality/assembly-instructions/${id}`, {
        method:'PUT', headers:{ Authorization:`Bearer ${token}`,'Content-Type':'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.message||data.error||'Save failed');
      setEntry(data.instruction); setForm(JSON.parse(JSON.stringify(data.instruction)));
      setEditing(false); setShowSaveModal(false); setChangeSummary('');
    } catch(err) { setError(err.message); } finally { setSaving(false); }
  };

  const handleAction = async action => {
    if (requestedRevision || snapshot) return;
    setSaving(true); setError('');
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/assembly-instructions/${id}/submit`, {
        method:'POST', headers:{ Authorization:`Bearer ${token}`,'Content-Type':'application/json' },
        body: JSON.stringify({ action, changeSummary: actionNote, expectedVersion:entry.__v }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.message||data.error||'Action failed');
      setEntry(data.instruction); setForm(JSON.parse(JSON.stringify(data.instruction)));
      setActionModal(null); setActionNote('');
    } catch(err) { setError(err.message); } finally { setSaving(false); }
  };

  const availableActions = entry && !requestedRevision && !snapshot ? Object.entries(TRANSITIONS)
    .filter(([,t]) => !t.from || t.from === entry.currentStatus)
    .map(([key,t]) => ({ key, label:t.label }))
    .filter(a => canManage
      || (supplierKey && a.key === 'submit' && entry?.createdBy === user?.id && entry?.currentStatus !== 'Released')
      || (supplierKey && a.key === 'revise' && entry?.currentStatus === 'Released')) : [];
  const canEditThis = !requestedRevision && !snapshot && entry?.currentStatus === 'Draft'
    && (canManage || (supplierKey && (entry?.createdBy === user?.id
      || ((entry?.visibility?.suppliers || []).includes(String(role).replace(/^test-/,''))
        && (entry?.revisions || []).some(r => r.status === 'Released')))));
  const unresolvedBlockers = (entry?.releaseBlockers || []).some(b => !b.resolved);

  if (loading) return <div style={{ padding:40,textAlign:'center',color:'var(--ni-text-muted)' }}>Loading…</div>;
  if (!entry) return <div role="alert" style={{ padding:30 }}>{error || 'Document not found.'} <button onClick={load}>Retry</button></div>;

  const inp = { background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',
    color:'var(--ni-text)',borderRadius:6,padding:'8px 12px',fontSize:13,width:'100%',boxSizing:'border-box' };
  const labelS = { display:'block',fontSize:11,fontWeight:700,letterSpacing:'0.06em',
    textTransform:'uppercase',color:'var(--ni-text-muted)',marginBottom:5 };
  const hint = { fontSize:11,color:'var(--ni-text-muted)',marginTop:5,lineHeight:1.4 };
  const readVal = v => <span style={{ fontSize:14 }}>{v||<span style={{ color:'var(--ni-text-muted)' }}>—</span>}</span>;
  const displaySteps = editing ? form.steps||[] : entry.steps||[];

  return (
    <div style={{ maxWidth:820 }}>
      {/* Header */}
      <div style={{ display:'flex',alignItems:'center',gap:12,marginBottom:6 }}>
        <button onClick={()=>navigate('/quality/assembly-instructions')}
          style={{ background:'none',border:'none',color:'var(--ni-text-muted)',cursor:'pointer',fontSize:20,padding:0 }}>←</button>
        <div>
          <div style={{ fontSize:11,fontFamily:'monospace',color:ACCENT,marginBottom:2 }}>{entry.documentNumber}</div>
          <h2 style={{ margin:0,fontSize:20,fontWeight:800 }}>{entry.name}</h2>
        </div>
        <div style={{ marginLeft:'auto',display:'flex',gap:8,alignItems:'center' }}>
          <StatusBadge status={entry.currentStatus} />
          <span style={{ fontSize:12,color:'var(--ni-text-muted)',fontFamily:'monospace' }}>Rev. {entry.currentRevision}</span>
        </div>
      </div>

      {error && <div style={{ color:'#EC2124',fontSize:13,margin:'12px 0',padding:'8px 12px',background:'#EC212411',borderRadius:6 }}>{error}</div>}

      <ProcedureRevisionBar entry={entry} requestedRevision={requestedRevision} revisions={availableRevisions} snapshot={snapshot}
        onRevisionChange={revision => setSearchParams(revision ? { revision } : {})} />
      <ProcedureAuthoringMetadata value={editing ? form : entry} editing={editing} onChange={set} staff={canManage} />

      {/* Action bar */}
      {(canEditThis || availableActions.length > 0) && (
        <div style={{ display:'flex',gap:8,marginBottom:20,flexWrap:'wrap' }}>
          {!editing ? (
            <>
              {canEditThis && <button onClick={()=>setEditing(true)} disabled={!canEditThis}
                style={{ background:ACCENT+'22',color:ACCENT,border:`1px solid ${ACCENT}44`,borderRadius:6,
                  padding:'6px 16px',fontSize:12,fontWeight:700,cursor:'pointer' }}>Edit</button>}
              {availableActions.map(a=>(
                <button key={a.key} disabled={saving || (a.key === 'approve' && unresolvedBlockers)} title={a.key === 'approve' && unresolvedBlockers ? 'Resolve release blockers before approval' : ''} onClick={()=>{setActionModal(a.key);setActionNote('');}}
                  style={{ background:'rgba(255,255,255,0.05)',border:'1px solid var(--ni-border-soft)',
                    color:'var(--ni-text)',borderRadius:6,padding:'6px 16px',fontSize:12,fontWeight:600,cursor:'pointer' }}>
                  {a.label}
                </button>
              ))}
            </>
          ) : (
            <>
              <button onClick={()=>setShowSaveModal(true)}
                style={{ background:'#46BB89',color:'#000',border:'none',borderRadius:6,
                  padding:'6px 16px',fontSize:12,fontWeight:700,cursor:'pointer' }}>Save Changes</button>
              <button onClick={()=>{ setEditing(false); setForm(JSON.parse(JSON.stringify(entry))); }}
                style={{ background:'transparent',border:'1px solid var(--ni-border-soft)',color:'var(--ni-text)',
                  borderRadius:6,padding:'6px 16px',fontSize:12,cursor:'pointer' }}>Cancel</button>
            </>
          )}
        </div>
      )}

      {/* Metadata */}
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:16 }}>
        <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:14 }}>
          <div style={labelS}>Product Family</div>
          {editing ? <ProductFamilyPicker value={form.productFamily} getToken={getToken}
              onChange={v=>setForm(f=>({...f, productFamily:v, productNumbers:[]}))} />
          : readVal(entry.productFamily)}
        </div>
        <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:14 }}>
          <div style={labelS}>Estimated Time</div>
          {editing ? <input type="number" value={form.estimatedMinutes||''} onChange={e=>set('estimatedMinutes',e.target.value)} style={inp} />
          : <span style={{ fontSize:14 }}>{entry.estimatedMinutes?`${entry.estimatedMinutes} min`:<span style={{ color:'var(--ni-text-muted)' }}>—</span>}</span>}
        </div>
        <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:14 }}>
          <div style={labelS}>Steps</div>
          <span style={{ fontSize:20,fontWeight:800,color:ACCENT }}>{entry.steps?.length||0}</span>
        </div>
      </div>

      {/* Product Numbers (scoped to the selected family, from product metadata) */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={labelS}>Product Numbers</div>
        {editing ? <ProductNumberPicker family={form.productFamily} value={form.productNumbers||[]}
            onChange={v=>set('productNumbers',v)} getToken={getToken} accent={ACCENT} />
        : (entry.productNumbers||[]).length ? (
          <div style={{ display:'flex',gap:6,flexWrap:'wrap',marginTop:4 }}>
            {entry.productNumbers.map(pn=>(
              <span key={pn} style={{ fontSize:12,fontWeight:700,fontFamily:'monospace',background:ACCENT+'22',color:ACCENT,borderRadius:20,padding:'3px 10px' }}>{pn}</span>
            ))}
          </div>
        ) : <span style={{ fontSize:14,color:'var(--ni-text-muted)' }}>—</span>}
      </div>

      {/* Description */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={labelS}>Description</div>
        {editing ? <textarea value={form.description||''} onChange={e=>set('description',e.target.value)}
          rows={3} style={{ ...inp,resize:'vertical' }} />
        : <p style={{ margin:0,fontSize:14,lineHeight:1.6 }}>{entry.description||<span style={{ color:'var(--ni-text-muted)' }}>—</span>}</p>}
      </div>

      {/* Workmanship, ESD & Safety */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={{ ...labelS, marginBottom:12 }}>Workmanship, ESD &amp; Safety</div>
        <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr 1fr',gap:12,marginBottom:12 }}>
          <div><div style={labelS}>Acceptance Standard</div>
            {editing ? <><StandardPicker value={form.acceptanceStandard} onChange={v=>set('acceptanceStandard',v)} getToken={getToken} accent={ACCENT} placeholder="Select standard…" />
              <div style={hint}>The single standard (with class) this build is accepted / rejected against — e.g. IPC-A-610.</div></>
            : readVal(entry.acceptanceStandard)}</div>
          <div><div style={labelS}>Class</div>
            {editing ? <select value={form.acceptanceClass||''} onChange={e=>set('acceptanceClass',e.target.value)} style={inp}>
              {ACCEPTANCE_CLASSES.map(c=><option key={c} value={c}>{c||'—'}</option>)}</select>
            : readVal(entry.acceptanceClass)}</div>
          <div><div style={labelS}>Effective Date</div>
            {editing ? <input type="date" value={form.effectiveDate?String(form.effectiveDate).slice(0,10):''} onChange={e=>set('effectiveDate',e.target.value)} style={inp} />
            : <span style={{ fontSize:14 }}>{entry.effectiveDate?new Date(entry.effectiveDate).toLocaleDateString():<span style={{ color:'var(--ni-text-muted)' }}>—</span>}</span>}</div>
        </div>
        <div style={{ marginBottom:12 }}><div style={labelS}>Referenced Standards</div>
          {editing ? <><StandardPicker multi value={Array.isArray(form.referencedStandards)?form.referencedStandards:csv(form.referencedStandards)} onChange={v=>set('referencedStandards',v)} getToken={getToken} accent={ACCENT} />
            <div style={hint}>Other standards this instruction follows or cites — e.g. J-STD-001 (soldering process), ANSI/ESD S20.20.</div></>
          : <span style={{ fontSize:14 }}>{(entry.referencedStandards||[]).join(', ')||<span style={{ color:'var(--ni-text-muted)' }}>—</span>}</span>}</div>
        <div style={{ marginBottom:12 }}>
          {editing ? (
            <label style={{ display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13 }}>
              <input type="checkbox" checked={!!form.esdSensitive} onChange={e=>set('esdSensitive',e.target.checked)} />
              <span style={{ fontWeight:700 }}>ESD-sensitive assembly</span>
            </label>
          ) : (
            <div style={labelS}>ESD {entry.esdSensitive ? <span style={{ color:'#EC2124' }}>— SENSITIVE</span> : <span style={{ color:'var(--ni-text-muted)' }}>— not flagged</span>}</div>
          )}
          {(editing ? form.esdSensitive : entry.esdSensitive) && (
            editing ? <div style={{ marginTop:8 }}>
              <OptionTagPicker value={form.esdControls||[]} onChange={v=>set('esdControls',v)} options={ESD_CONTROLS} accent={ACCENT} placeholder="+ Add ESD control…" />
              <input value={form.esdRequirements||''} onChange={e=>set('esdRequirements',e.target.value)} placeholder="Additional ESD notes (optional)" style={{ ...inp,marginTop:8 }} />
            </div>
            : <div style={{ marginTop:4 }}>
              <ChipList items={entry.esdControls} accent={ACCENT} />
              {entry.esdRequirements && <p style={{ margin:'6px 0 0',fontSize:13 }}>{entry.esdRequirements}</p>}
            </div>
          )}
        </div>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
          <div><div style={labelS}>Required PPE</div>
            {editing ? <OptionTagPicker value={form.safety?.ppe||[]} onChange={v=>setSafety('ppe',v)} options={PPE_OPTIONS} accent={ACCENT} placeholder="+ Add PPE…" />
            : <ChipList items={entry.safety?.ppe} accent={ACCENT} />}</div>
          <div><div style={labelS}>Required Competencies</div>
            {editing ? <OptionTagPicker value={form.requiredCompetencies||[]} onChange={v=>set('requiredCompetencies',v)} options={COMPETENCY_OPTIONS} accent={ACCENT} placeholder="+ Add competency…" />
            : <ChipList items={entry.requiredCompetencies} accent={ACCENT} />}</div>
        </div>
        <div style={{ marginTop:12 }}><div style={labelS}>Hazard Notes</div>
          {editing ? <textarea value={form.safety?.hazardNotes||''} onChange={e=>setSafety('hazardNotes',e.target.value)} rows={2} style={{ ...inp,resize:'vertical' }} />
          : <p style={{ margin:0,fontSize:14,lineHeight:1.6 }}>{entry.safety?.hazardNotes||<span style={{ color:'var(--ni-text-muted)' }}>—</span>}</p>}</div>
      </div>

      {/* Tools & Equipment */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={{ marginBottom:12 }}><div style={labelS}>Tools &amp; Equipment</div></div>
        <ToolEditor tools={editing ? (form.tools||[]) : (entry.tools||[])}
          onChange={v=>set('tools',v)} getToken={getToken} accent={ACCENT} readOnly={!editing}
          steps={editing ? (form.steps||[]) : (entry.steps||[])} />
      </div>

      {/* Assembly Steps */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14 }}>
          <div style={labelS}>Assembly Steps</div>
          {editing && (
            <button type="button" onClick={addStep}
              style={{ background:ACCENT+'22',color:ACCENT,border:`1px solid ${ACCENT}44`,borderRadius:6,
                padding:'4px 14px',fontSize:12,fontWeight:700,cursor:'pointer' }}>+ Step</button>
          )}
        </div>
        {displaySteps.length===0 ? (
          <div style={{ fontSize:14,color:'var(--ni-text-muted)',textAlign:'center',padding:'20px 0' }}>No assembly steps defined.</div>
        ) : displaySteps.map((step,i)=>(
          <div key={i} style={{ border:'1px solid var(--ni-border-soft)',borderRadius:8,padding:14,marginBottom:10,
            background:'rgba(45,212,191,0.03)' }}>
            <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10 }}>
              <span style={{ fontSize:12,fontWeight:700,color:ACCENT,letterSpacing:'0.05em' }}>STEP {step.stepNumber}</span>
              {editing && <button type="button" onClick={()=>removeStep(i)}
                style={{ background:'none',border:'none',color:'#EC2124',cursor:'pointer',fontSize:16 }}>×</button>}
            </div>
            {editing ? (
              <>
                <div style={{ marginBottom:8 }}><div style={labelS}>Title</div>
                  <input value={step.title||''} onChange={e=>setStep(i,'title',e.target.value)} style={inp} /></div>
                <div style={{ marginBottom:8 }}><div style={labelS}>Instruction</div>
                  <textarea value={step.instruction||''} onChange={e=>{ cursorRef.current[i]=e.target.selectionStart; setStep(i,'instruction',e.target.value); }}
                    onSelect={e=>{ cursorRef.current[i] = e.target.selectionStart; }}
                    onKeyUp={e=>{ cursorRef.current[i] = e.target.selectionStart; }}
                    onClick={e=>{ cursorRef.current[i] = e.target.selectionStart; }}
                    onDrop={e=>{ const id=e.dataTransfer.getData('toolId'); if(id){ e.preventDefault(); cursorRef.current[i]=e.target.selectionStart; insertToolRef(i,id); } }}
                    rows={2} style={{ ...inp,resize:'vertical' }} />
                  {(form.tools||[]).length > 0 && (
                    <div style={{ display:'flex',gap:6,flexWrap:'wrap',alignItems:'center',marginTop:6 }}>
                      <span style={{ fontSize:11,color:'var(--ni-text-muted)' }}>Reference a tool:</span>
                      {(form.tools||[]).filter(t=>t.id && t.name).map(t=>(
                        <button type="button" key={t.id} draggable
                          onDragStart={e=>e.dataTransfer.setData('toolId', t.id)}
                          onClick={()=>insertToolRef(i, t.id)}
                          title="Click to insert at cursor, or drag into the text"
                          style={{ fontSize:11,fontWeight:700,color:ACCENT,background:ACCENT+'18',border:`1px solid ${ACCENT}44`,
                            borderRadius:10,padding:'2px 8px',cursor:'pointer' }}>🔧 {t.name}</button>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:8 }}>
                  <div><div style={labelS}>Caution</div>
                    <input value={step.caution||''} onChange={e=>setStep(i,'caution',e.target.value)} style={inp} /></div>
                  <StepImageInput instructionId={id} value={step.imageUrl||''}
                    onChange={url=>setStep(i,'imageUrl',url)} getToken={getToken} accent={ACCENT} />
                </div>
                <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr',gap:10 }}>
                  <div><div style={labelS}>Acceptance Criteria</div>
                    <input value={step.acceptanceCriteria||''} onChange={e=>setStep(i,'acceptanceCriteria',e.target.value)}
                      placeholder="In-process check" style={inp} /></div>
                  <div><div style={labelS}>Torque</div>
                    <input value={step.torqueSpec||''} onChange={e=>setStep(i,'torqueSpec',e.target.value)} style={inp} /></div>
                </div>
                <label style={{ display:'flex',alignItems:'center',gap:6,fontSize:12,cursor:'pointer',marginTop:8 }}>
                  <input type="checkbox" checked={!!step.requiresInspection} onChange={e=>setStep(i,'requiresInspection',e.target.checked)} />
                  Requires inspection sign-off
                </label>
                <div style={{ marginTop:8 }}><div style={labelS}>Linked Quality Requirement</div>
                  <RequirementPicker value={step.requirementRef} onChange={r=>setStep(i,'requirementRef',r)} getToken={getToken} accent={ACCENT} /></div>
              </>
            ) : (
              <div>
                {step.title && <p style={{ margin:'0 0 6px',fontSize:14,fontWeight:700 }}>{step.title}</p>}
                <p style={{ margin:'0 0 8px',fontSize:14,lineHeight:1.7 }}>{step.instruction ? renderInstruction(step.instruction, entry.tools, ACCENT) : '—'}</p>
                {step.imageUrl && (
                  <img src={step.imageUrl} alt={step.title||`Step ${step.stepNumber}`}
                    style={{ maxWidth:'100%',borderRadius:6,marginBottom:8,border:'1px solid var(--ni-border-soft)' }} />
                )}
                {step.acceptanceCriteria && (
                  <p style={{ margin:'0 0 4px',fontSize:13 }}>
                    <strong style={{ color:ACCENT }}>✓ Accept:</strong> {step.acceptanceCriteria}
                    {step.requiresInspection && <span style={{ marginLeft:8,fontSize:10,fontWeight:700,color:'#60a5fa',background:'#60a5fa18',borderRadius:4,padding:'1px 6px' }}>INSPECTION</span>}
                  </p>
                )}
                {step.torqueSpec && <p style={{ margin:'0 0 4px',fontSize:13,color:'var(--ni-text-muted)' }}>Torque: <strong style={{ color:'var(--ni-text)' }}>{step.torqueSpec}</strong></p>}
                {step.requirementRef?.requirementId && (
                  <p style={{ margin:'0 0 4px',fontSize:12,color:'var(--ni-text-muted)' }}>
                    <span style={{ fontSize:10,fontWeight:700,color:'#818cf8',background:'#818cf820',borderRadius:4,padding:'1px 6px',marginRight:6 }}>REQ</span>
                    <span style={{ fontFamily:'monospace',color:'#818cf8' }}>{step.requirementRef.documentNumber}{step.requirementRef.sectionNumber?` · ${step.requirementRef.sectionNumber}`:''}</span>
                    {step.requirementRef.title ? <> — {step.requirementRef.title}</> : null}
                  </p>
                )}
                {step.caution && (
                  <div style={{ marginTop:4,padding:'6px 10px',background:'#f59e0b11',border:'1px solid #f59e0b33',borderRadius:6,fontSize:12,color:'#f59e0b' }}>
                    ⚠ {step.caution}
                  </div>
                )}
                {!step.acceptanceCriteria && step.requiresInspection && (
                  <span style={{ fontSize:10,fontWeight:700,color:'#60a5fa',background:'#60a5fa18',borderRadius:4,padding:'1px 6px' }}>INSPECTION</span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Notes */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={labelS}>Notes</div>
        {editing ? <textarea value={form.notes||''} onChange={e=>set('notes',e.target.value)}
          rows={2} style={{ ...inp,resize:'vertical' }} />
        : <p style={{ margin:0,fontSize:14,lineHeight:1.6 }}>{entry.notes||<span style={{ color:'var(--ni-text-muted)' }}>—</span>}</p>}
      </div>

      {/* Supplier visibility */}
      {canManage && (
        <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
          <div style={labelS}>Supplier Visibility</div>
          <div style={{ display:'flex',gap:8,flexWrap:'wrap',marginTop:4 }}>
            {SUPPLIERS.map(s=>(
              editing ? (
                <button type="button" key={s} onClick={()=>toggleSupplier(s)}
                  style={{ padding:'5px 14px',borderRadius:20,fontSize:12,fontWeight:700,cursor:'pointer',border:'1px solid',
                    background:(form.visibility?.suppliers||[]).includes(s)?ACCENT+'22':'transparent',
                    color:(form.visibility?.suppliers||[]).includes(s)?ACCENT:'var(--ni-text-muted)',
                    borderColor:(form.visibility?.suppliers||[]).includes(s)?ACCENT:'var(--ni-border-soft)' }}>
                  {s.toUpperCase()}
                </button>
              ) : (
                <span key={s} style={{ padding:'3px 12px',borderRadius:20,fontSize:11,fontWeight:700,
                  background:(entry.visibility?.suppliers||[]).includes(s)?ACCENT+'22':'rgba(255,255,255,0.04)',
                  color:(entry.visibility?.suppliers||[]).includes(s)?ACCENT:'var(--ni-text-muted)' }}>
                  {s.toUpperCase()}
                </span>
              )
            ))}
          </div>
        </div>
      )}

      {/* Linked Jigs */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={labelS}>Linked Jigs</div>
        <ResourcePicker
          endpoint="/quality/jigs" responseKey="jigs"
          items={editing ? form.linkedJigs||[] : entry.linkedJigs||[]}
          onChange={v=>set('linkedJigs',v)}
          getToken={getToken} color="#a78bfa" label="jigs"
          disabled={!editing} detailPath="/quality/jigs"
        />
      </div>

      {/* Linked Equipment */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={labelS}>Linked Equipment</div>
        <ResourcePicker
          endpoint="/quality/equipment" responseKey="items"
          items={editing ? form.linkedEquipment||[] : entry.linkedEquipment||[]}
          onChange={v=>set('linkedEquipment',v)}
          getToken={getToken} color="#f59e0b" label="equipment"
          disabled={!editing} detailPath="/quality/equipment"
        />
      </div>

      {/* Linked Documents */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={labelS}>Linked Documents</div>
        <ResourcePicker
          endpoint="/quality/documents" responseKey="documents"
          items={editing ? form.linkedDocuments||[] : entry.linkedDocuments||[]}
          onChange={v=>set('linkedDocuments',v)}
          getToken={getToken} color="#60a5fa" label="documents"
          disabled={!editing} detailPath="/quality/documents"
        />
      </div>

      {/* Used In Flows */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={labelS}>Used In Process Flows</div>
        {usedInFlows.length===0
          ? <div style={{ fontSize:13,color:'var(--ni-text-muted)',marginTop:4 }}>Not linked to any flow yet.</div>
          : usedInFlows.map(f=>(
            <div key={f._id} onClick={()=>navigate(`/quality/process/${f._id}`)}
              style={{ display:'flex',gap:10,alignItems:'center',padding:'7px 0',
                borderBottom:'1px solid var(--ni-border-soft)',cursor:'pointer' }}
              onMouseEnter={e=>e.currentTarget.style.opacity='0.75'}
              onMouseLeave={e=>e.currentTarget.style.opacity='1'}>
              <span style={{ fontFamily:'monospace',fontSize:11,color:'#60a5fa',flexShrink:0 }}>{f.documentNumber}</span>
              <span style={{ fontSize:13,flex:1 }}>{f.name}</span>
              <StatusBadge status={f.currentStatus}/>
            </div>
          ))
        }
      </div>

      {/* Original source evidence */}
      {entry.source?.url && (
        <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
          <div style={labelS}>Original source evidence</div>
          <a href={entry.source.url} target="_blank" rel="noopener noreferrer"
            style={{ fontSize:13,color:ACCENT,textDecoration:'none',wordBreak:'break-word' }}>
            {(entry.source.system||'SharePoint')}{entry.source.fileName?` — ${entry.source.fileName}`:''} ↗
          </a>
          {entry.source.externalModifiedAt && <div style={{ fontSize:11,color:'var(--ni-text-muted)',marginTop:4 }}>Updated {new Date(entry.source.externalModifiedAt).toLocaleDateString()}</div>}
        </div>
      )}

      {/* Revision history */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16 }}>
        <div style={labelS}>Revision History</div>
        <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12,marginTop:8 }}>
          <thead>
            <tr style={{ color:'var(--ni-text-muted)',fontSize:10,textTransform:'uppercase',letterSpacing:'0.06em' }}>
              {['Rev','Status','Summary','By','Approved by','Date'].map(h=>(
                <th key={h} style={{ padding:'6px 10px',textAlign:'left',fontWeight:600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...(entry.revisions||[])].reverse().map((r,i)=>(
              <tr key={i} style={{ borderTop:'1px solid var(--ni-border-soft)' }}>
                <td style={{ padding:'8px 10px',fontFamily:'monospace' }}>{r.revision}</td>
                <td style={{ padding:'8px 10px' }}><StatusBadge status={r.status}/></td>
                <td style={{ padding:'8px 10px',color:'var(--ni-text-muted)' }}>{r.changeSummary}</td>
                <td style={{ padding:'8px 10px',color:'var(--ni-text-muted)' }}>{r.updatedByName}</td>
                <td style={{ padding:'8px 10px',color:'var(--ni-text-muted)' }}>
                  {r.approvedByName ? `${r.approvedByName}${r.approvedAt?` · ${new Date(r.approvedAt).toLocaleDateString()}`:''}` : '—'}
                </td>
                <td style={{ padding:'8px 10px',color:'var(--ni-text-muted)',whiteSpace:'nowrap' }}>
                  {r.updatedAt?new Date(r.updatedAt).toLocaleDateString():'—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Save modal */}
      {showSaveModal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000 }}>
          <div style={{ background:'var(--ni-bg)',border:'1px solid var(--ni-border-soft)',borderRadius:12,padding:28,width:420 }}>
            <h3 style={{ margin:'0 0 16px',fontSize:16 }}>Save Changes</h3>
            <div style={labelS}>Change Summary</div>
            <input value={changeSummary} onChange={e=>setChangeSummary(e.target.value)}
              placeholder="Describe what changed…" style={{ ...inp,marginBottom:20 }} />
            <div style={{ display:'flex',gap:10 }}>
              <button onClick={handleSave} disabled={saving}
                style={{ background:'#46BB89',color:'#000',border:'none',borderRadius:6,padding:'9px 20px',fontWeight:700,fontSize:13,cursor:'pointer' }}>
                {saving?'Saving…':'Save'}
              </button>
              <button onClick={()=>setShowSaveModal(false)}
                style={{ background:'transparent',border:'1px solid var(--ni-border-soft)',color:'var(--ni-text)',borderRadius:6,padding:'9px 16px',fontSize:13,cursor:'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Action modal */}
      {actionModal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000 }}>
          <div style={{ background:'var(--ni-bg)',border:'1px solid var(--ni-border-soft)',borderRadius:12,padding:28,width:420 }}>
            <h3 style={{ margin:'0 0 16px',fontSize:16 }}>{TRANSITIONS[actionModal]?.label}</h3>
            <div style={labelS}>Note (optional)</div>
            <input value={actionNote} onChange={e=>setActionNote(e.target.value)}
              placeholder="Reason or comment…" style={{ ...inp,marginBottom:20 }} />
            <div style={{ display:'flex',gap:10 }}>
              <button onClick={()=>handleAction(actionModal)} disabled={saving}
                style={{ background:'#46BB89',color:'#000',border:'none',borderRadius:6,padding:'9px 20px',fontWeight:700,fontSize:13,cursor:'pointer' }}>
                {saving?'Processing…':'Confirm'}
              </button>
              <button onClick={()=>setActionModal(null)}
                style={{ background:'transparent',border:'1px solid var(--ni-border-soft)',color:'var(--ni-text)',borderRadius:6,padding:'9px 16px',fontSize:13,cursor:'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
